import { createGlobalStyle } from 'styled-components'

export const AntOverrides = createGlobalStyle`
  // buttons
  .ant-btn.ant-btn-primary {
    background-color: ${({ theme }) => theme.colour.theme.primaryColour};
    border-color: ${({ theme }) => theme.colour.theme.primaryColour};
    color: ${({ theme }) => theme.colour.theme.buttonTextColour};
  }

  // switches
  .ant-switch.ant-switch-checked {
    background-color: ${({ theme }) => theme.colour.theme.primaryColour};

    & .ant-switch-inner {
      color: ${({ theme }) => theme.colour.theme.buttonTextColour};
    }
  }

  // make table header text bold
  .ant-table-thead > tr > th {
    font-weight: 600;
  }

  // set font styles of all buttons
  .ant-btn {
    ${({ theme }) => theme.font.label02}
    font-weight: 600;
  }

  // set hover, focus and active styles of "secondary" ant button
  .ant-btn-default.ant-btn {
    &:hover, &:focus  {
      color: ${({ theme }) => theme.colour.secondary.base};
      border-color: ${({ theme }) => theme.colour.secondary._400};
      background-color: ${({ theme }) => theme.colour.neutral.surface};
    }

    &:active {
      background-color: ${({ theme }) => theme.colour.secondary._50};
    }
  }

  // make dropdown menu rounder (no variable for it)
  .ant-dropdown-menu {
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }

  hr {
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.colour.neutral.border};
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  // align the checkbox with the label. Without this the label sits too low
  .ant-checkbox-wrapper {
    align-items: center;

    & > .ant-checkbox {
      top: 2px; // this puts the checkbox in the middle of the label
      align-self: baseline;
    }
  }

  @media (max-width: 575px) {
    .ant-form-item .ant-form-item-label > label {
      height: auto; // horizontal forms in small devices have a massive height unless this is added
    }
  }

  // Override ant form help and extea text styling for ✨ a e s t h e t i c s ✨
  .ant-form-item-explain,
  .ant-form-item-extra  {
    ${({ theme }) => theme.font.caption01}
    color: ${({ theme }) => theme.colour.secondary._400};
    margin-bottom: 0.5rem;
  }

  .ant-picker-panel-container {
    border-radius: ${({ theme }) => theme.borderRadius.large};

    .ant-picker-footer {
      a,
      button {
        border: 1px solid ${({ theme }) => theme.colour.link.base};
        border-radius: ${({ theme }) => theme.borderRadius.max};
        padding: 5px 10px;
        line-height: normal;
        color: ${({ theme }) => theme.colour.link.base};
        background-color: transparent;
        font-size: inherit;
      }

      .ant-picker-footer-extra {
        div:empty {
          display: none;
        }
      }
    }
  }

  .ant-picker-panel {
    .ant-picker-footer {
      border-top: none;
      padding: 10px 20px 20px;
      display: flex;

      .ant-picker-ranges {
        padding: 0;
      }
    }
  }

  // time picker
  .ant-picker-time-panel {
    .ant-picker-content {
      padding: 10px 20px;
    }

    .ant-picker-time-panel-column {
      width: auto;
      overflow-x: hidden;
      scrollbar-width: thin;
      border-bottom: 1px solid ${({ theme }) => theme.colour.secondary._100};

      .ant-picker-time-panel-cell {
        padding: 7.5px 0;

        .ant-picker-time-panel-cell-inner {
          padding: 10px 20px;
          margin: 0 5px;
          display: inline;
          border-radius: ${({ theme }) => theme.borderRadius.large};
        }

        &.ant-picker-time-panel-cell-selected {
          .ant-picker-time-panel-cell-inner {
            background-color: ${({ theme }) => theme.colour.theme.primaryColour};
          }
        }
      }

      &:after {
        content: none;
      }
    }
  }

  // date picker
  .ant-picker-date-panel {
    width: auto;

    .ant-picker-header {
      border: none;
      padding: 10px 24px;
      font-size: 16px;
    }

    .ant-picker-body {
      padding: 10px 20px;

      .ant-picker-content {
        width: auto;

        .ant-picker-cell {
          &.ant-picker-cell-selected,
          &.ant-picker-cell-range-start,
          &.ant-picker-cell-range-end {
            .ant-picker-cell-inner {
              background-color: ${({ theme }) => theme.colour.primary._600};
              color: ${({ theme }) => theme.colour.neutral.white};
            }
          }

          &.ant-picker-cell-in-range::before {
            background-color: ${({ theme }) => theme.colour.primary._100};
          }

          .ant-picker-cell-inner {
            min-width: 42px;
            height: 42px;
            line-height: 42px;
            border-radius: ${({ theme }) => theme.borderRadius.max};
          }
        }

        .ant-picker-cell-today {
          .ant-picker-cell-inner:before {
            border: 1px solid ${({ theme }) => theme.colour.primary._600};
            border-radius: ${({ theme }) => theme.borderRadius.max};
          }
        }
      }
    }
  }

  // range picker
  .ant-picker-dropdown-range {
    .ant-picker-date-panel {
      .ant-picker-cell:not(.ant-picker-cell-in-view) {
        pointer-events: none;
        
        &::before {
          display: none;
        }

        .ant-picker-cell-inner {
          display: none;
        }
      }
      
      .ant-picker-cell-range-hover::after {
        border-color: ${({ theme }) => theme.colour.primary._600};
      }

      .ant-picker-cell-range-hover-start::after,
      .ant-picker-cell-range-hover-end::after {
        border-top: 1px dashed !important;
        border-bottom: 1px dashed !important;
        border-color: ${({ theme }) => theme.colour.primary._600} !important;
      }
    }

    .ant-picker-footer {
      .ant-picker-preset {
        .ant-tag {
          border: 1px solid ${({ theme }) => theme.colour.primary._600};
          border-radius: ${({ theme }) => theme.borderRadius.max};
          color: ${({ theme }) => theme.colour.link.base};
          padding: 5px 10px;
          line-height: normal;
          background-color: transparent;
        }
      }
    }
  }

  // week picker
  .ant-picker-week-panel {
    width: auto;

    .ant-picker-header {
      border: none;
      padding: 10px 24px;
      font-size: 16px;
    }

    .ant-picker-body {
      padding: 10px 20px;

      .ant-picker-content {
        width: auto;

        .ant-picker-week-panel-row {
          td {
            &:first-child {
              border-top-left-radius: ${({ theme }) => theme.borderRadius.large};
              border-bottom-left-radius: ${({ theme }) => theme.borderRadius.large};
            }

            &:last-child {
              border-top-right-radius: ${({ theme }) => theme.borderRadius.large};
              border-bottom-right-radius: ${({ theme }) => theme.borderRadius.large};
            }

            &:hover {
              background-color: ${({ theme }) => theme.colour.primary._100};
            }
          }

          &.ant-picker-week-panel-row-selected {
            td {
              background-color: ${({ theme }) => theme.colour.primary._600};
            }
          }
        }

        .ant-picker-cell {
          &.ant-picker-cell-week {
            font-weight: bolder;
            border-right: 1px dotted ${({ theme }) => theme.colour.primary._100};
          }

          .ant-picker-cell-inner {
            min-width: 42px;
            height: 42px;
            line-height: 42px;

            &:before {
              border: 1px solid ${({ theme }) => theme.colour.primary._600};
              border-radius: ${({ theme }) => theme.borderRadius.max};
            }
          }
        }
      }
    }
  }

  // month, quarter and year picker
  .ant-picker-month-panel,
  .ant-picker-quarter-panel,
  .ant-picker-year-panel {
    width: auto;

    .ant-picker-header {
      border: none;
      padding: 10px 24px;
      font-size: 16px;
    }

    .ant-picker-body {
      padding: 10px 20px;

      .ant-picker-content {
        width: auto;

        .ant-picker-cell {
          &.ant-picker-cell-selected,
          &.ant-picker-cell-range-start,
          &.ant-picker-cell-range-end {
            .ant-picker-cell-inner {
              background-color: ${({ theme }) => theme.colour.primary._600};
              color: ${({ theme }) => theme.colour.neutral.white};
            }
          }

          &.ant-picker-cell-in-range::before {
            background-color: transparent;
          }

          &.ant-picker-cell-in-range {
            .ant-picker-cell-inner {
              background-color: ${({ theme }) => theme.colour.primary._100};
              border-radius: unset;
            }
          }

          .ant-picker-cell-inner {
            min-width: 84px;
            height: 42px;
            line-height: 42px;

            &:before {
              border: 1px solid ${({ theme }) => theme.colour.primary._600};
              border-radius: ${({ theme }) => theme.borderRadius.max};
            }
          }
        }
      }
    }
  }

  .ant-picker-ok {
    display: none !important; // override antd's default display: inline-block
  }
  
  @media (max-width: 767px) {
    .ant-picker-dropdown {
      &:after {
        content: '';
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: -1;
      }

      max-height: 75vh;
      overflow-y: auto;
      bottom: 0;
      left: 0 !important;
      top: auto !important;
      position: fixed;
      width: 100%;
      right: 0 !important;
      z-index: 9999;
      box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.2);

      .ant-picker-panel-container {
        display: flex;
        justify-content: center;
        flex-wrap: wrap-reverse;
      }
      
      .ant-picker-time-panel-column {
        opacity: 0.5;
        transition: opacity 0.3s ease;

        &:active,
        &:hover {
          opacity: 1;
          overscroll-behavior: contain;
        }
      }

      // range picker
      &.ant-picker-dropdown-range {
        padding: 0;

        .ant-picker-range-wrapper {
          &.ant-picker-time-range-wrapper {
            min-width: 100% !important;

            .ant-picker-panel-container {
              width: 100%;
            }
          }

          .ant-picker-range-arrow {
            display: none;
          }

          .ant-picker-panels {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
          }

          .ant-picker-footer {
            .ant-picker-footer-extra,
            .ant-picker-ranges {
              padding: 10px 20px;
            }
          }
        }
      }

      // date time picker
      .ant-picker-datetime-panel {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        .ant-picker-date-panel {
          border-bottom: 1px solid ${({ theme }) => theme.colour.secondary._100};
        }

        .ant-picker-time-panel {
          border: none;
        }
      }
    }

    // Prevent body scrolling when picker is open
    body:has(.ant-picker-dropdown-placement-bottomLeft:not(.ant-picker-dropdown-hidden)),
    body:has(.ant-picker-dropdown-placement-bottomRight:not(.ant-picker-dropdown-hidden)),
    body:has(.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)) {
      overflow: hidden !important;
      position: fixed;
      width: 100%;
      height: 100%;
    }
  }
`
